import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Play, Radio, RotateCcw, Square } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  CONNECTEE, SANS_CONTACT, VERDICT_CONSIGNE,
  ancienneteTexte, etatJoignabilite, libelleMachine, titreJoignabilite,
} from '@/shared/utils/liveness';

/**
 * Panneau de pilotage d'une batterie — partagé par le Monitoring et l'écran de Pilotage.
 *
 * UNE seule consigne de puissance, partagée par « Charger » et « Décharger ». Deux champs
 * séparés obligeaient à ressaisir la même valeur pour tester l'un puis l'autre, et
 * laissaient croire que charge et décharge étaient deux réglages indépendants.
 *
 * Composant unique pour les deux écrans : c'est la divergence entre eux qui a fait perdre
 * la consigne de puissance côté Pilotage. Elle ne peut plus se reproduire.
 *
 * Ce composant ne décide de RIEN : la liste des commandes vient de `actions`, c'est-à-dire
 * du catalogue serveur. Une action retirée du backend disparaît d'elle-même.
 *
 * @param {object}   actions       catalogue Batterie ({ code: définition }) — filtre l'affichage
 * @param {function} onAction      (code, valeur|null) => void
 * @param {string}   variant       'inline' (ligne de Pilotage) · 'panel' (colonne du Monitoring)
 * @param {string}   etatConnexion 'connectee' | 'deconnectee' | 'sans_contact'
 * @param {number}   ageS          ancienneté de la dernière mesure, en secondes
 * @param {string}   mqttStatus    ancien champ à deux valeurs — repli si `etatConnexion` manque
 * @param {object}   consigne      bloc consigne maintenue (consigne_w, consigne_verdict, …)
 */
export default function BatteryControlPanel({
  actions = {}, onAction, busy, variant = 'inline', defaultWatts = 500, mqttStatus,
  etatConnexion, ageS, consigne,
}) {
  // Consigne unique, en watts — l'unité du matériel (power_ctrl attend des W signés).
  const [watts, setWatts] = useState(String(defaultWatts));

  const has = code => !!actions[code];
  const label = (code, fallback) => actions[code]?.label || fallback;
  const noValue = watts === '' || Number.isNaN(parseFloat(watts));
  const fire = (code, withValue) => onAction(code, withValue ? parseFloat(watts) : null);

  const field = (
    <div className={variant === 'panel' ? '' : 'flex items-center gap-1.5'}>
      <Label className={variant === 'panel'
        ? 'text-[11px] text-neutral-500'
        : 'text-[11px] text-neutral-600 whitespace-nowrap'}>
        Puissance
      </Label>
      <div className="flex items-center gap-1">
        <Input
          type="number"
          value={watts}
          onChange={e => setWatts(e.target.value)}
          disabled={busy}
          className={variant === 'panel' ? 'h-8 text-[12px]' : 'h-7 w-[86px] text-[11px]'}
          data-testid="battery-power-input"
        />
        <span className="text-[11px] text-neutral-400">W</span>
      </div>
    </div>
  );

  const charge = has('charge_start') && (
    <Button
      size="sm"
      className={variant === 'panel' ? 'h-8 text-[11px]' : 'h-7 text-[11px]'}
      disabled={busy || noValue}
      onClick={() => fire('charge_start', true)}
      data-testid="battery-charge-btn"
    >
      <Play className="w-3 h-3 mr-1" /> {label('charge_start', 'Charger')}
    </Button>
  );

  const decharge = has('decharge_start') && (
    <Button
      size="sm" variant="outline"
      className={variant === 'panel' ? 'h-8 text-[11px]' : 'h-7 text-[11px]'}
      disabled={busy || noValue}
      onClick={() => fire('decharge_start', true)}
      data-testid="battery-discharge-btn"
    >
      <Play className="w-3 h-3 mr-1" /> {label('decharge_start', 'Décharger')}
    </Button>
  );

  const auto = has('auto') && (
    <Button
      size="sm" variant="outline"
      className={variant === 'panel' ? 'w-full h-8 text-[11px]' : 'h-7 text-[11px]'}
      disabled={busy}
      onClick={() => fire('auto', false)}
      data-testid="battery-auto-btn"
    >
      <RotateCcw className="w-3 h-3 mr-1" /> {label('auto', 'Retour au mode automatique')}
    </Button>
  );

  // Conservé tant que « OFF » n'est pas tranché : c'est la seule commande du matériel qui
  // s'en approche, et la retirer ferait perdre une capacité réelle en attendant.
  const zero = has('power_zero') && (
    <Button
      size="sm" variant="outline"
      className={`${variant === 'panel' ? 'w-full h-8' : 'h-7'} text-[11px] text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700`}
      disabled={busy}
      title={actions.power_zero?.note}
      onClick={() => fire('power_zero', false)}
      data-testid="battery-zero-btn"
    >
      <Square className="w-3 h-3 mr-1" /> {label('power_zero', 'Puissance 0 W')}
    </Button>
  );

  const restart = has('restart') && (
    <Button
      size="sm" variant="outline"
      className={variant === 'panel' ? 'w-full h-8 text-[11px]' : 'h-7 text-[11px]'}
      disabled={busy}
      title={actions.restart?.warning}
      onClick={() => fire('restart', false)}
      data-testid="battery-restart-btn"
    >
      <RotateCcw className="w-3 h-3 mr-1" /> {label('restart', 'Redémarrer')}
    </Button>
  );

  // Consigne que Flexcore republie en boucle. Sans ce rappel à l'écran, rien ne
  // distingue « la batterie fait ça d'elle-même » de « c'est nous qui l'imposons ».
  //
  // Le verdict est collé à ce rappel, et non ailleurs : « consigne tenue par Flexcore »
  // seul se lit comme une réussite. C'est ce malentendu qui a laissé republier 240 fois
  // un ordre de charge à une machine qui ne l'entendait pas.
  const minutes = consigne ? Math.floor((consigne.consigne_depuis_s || 0) / 60) : 0;
  const bandeauConsigne = consigne && (
    <div
      className="text-[11px] text-cyan-800 bg-cyan-50 border border-cyan-100 rounded-md px-2 py-1 space-y-0.5"
      title="Flexcore republie cette consigne toutes les 30 secondes. Sans ce rappel, la batterie reprend sa propre régulation au bout de quelques minutes."
      data-testid="battery-hold"
    >
      <div className="flex items-center gap-1.5">
        <Radio className="w-3 h-3 flex-shrink-0" />
        <span>
          Consigne tenue par Flexcore : <strong>{Math.abs(consigne.consigne_w)} W</strong>
          {minutes > 0 && <span className="text-cyan-600"> depuis {minutes} min</span>}
        </span>
      </div>
      <VerdictConsigne consigne={consigne} />
    </div>
  );

  // Témoin demandé par David : « est-ce que j'ai un retour comme quoi savoir si la
  // machine est bien allumée ? ». Une machine éteinte cesse d'envoyer ses mesures et
  // bascule en injoignable — c'est la seule preuve dont on dispose, autant la nommer
  // ainsi plutôt que « connecté », qui parle de réseau.
  //
  // Un cas de repli en plus des deux états : une machine dont aucun contact n'est
  // enregistré n'est pas une machine tombée, elle ne se règle pas avec le client.
  const etat = etatConnexion || etatJoignabilite({ mqtt_status: mqttStatus });
  const enLigne = etat === CONNECTEE;
  const ton = enLigne ? 'text-emerald-700'
    : etat === SANS_CONTACT ? 'text-neutral-600' : 'text-red-600';
  const point = enLigne ? 'bg-emerald-500'
    : etat === SANS_CONTACT ? 'bg-neutral-400' : 'bg-red-500';
  const temoin = etat && (
    <div
      className={`flex items-center gap-1.5 text-[11px] ${ton}`}
      title={titreJoignabilite(etat, ageS)}
      data-testid="battery-machine-status"
    >
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${point}`} />
      {libelleMachine(etat, ageS)}
    </div>
  );

  if (variant === 'panel') {
    return (
      <div className="space-y-2.5" data-testid="battery-controls">
        {temoin}
        {bandeauConsigne}
        {field}
        <div className="grid grid-cols-2 gap-2">{charge}{decharge}</div>
        {auto}
        {zero}
        {restart}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="battery-controls">
      {temoin}
      {bandeauConsigne}
      <div className="flex items-center gap-1.5 bg-white rounded-md border border-neutral-200/80 px-2 py-1">
        {field}
      </div>
      {charge}
      {decharge}
      <span className="w-px h-5 bg-neutral-200" />
      {auto}
      {zero}
      {restart}
    </div>
  );
}

/**
 * Ce que la batterie fait de la consigne, en clair.
 *
 * Trois issues, et surtout deux qui ne doivent jamais se confondre : « elle ne suit pas
 * encore » (elle nous parle, laissons-lui le temps) et « elle est injoignable » (elle ne
 * nous parle plus, la consigne tombe dans le vide et il faut appeler le client).
 */
function VerdictConsigne({ consigne }) {
  const verdict = VERDICT_CONSIGNE[consigne.consigne_verdict];
  if (!verdict) return null;

  const TONS = {
    ok: { classe: 'text-emerald-700', Icone: CheckCircle2 },
    attente: { classe: 'text-amber-700', Icone: Clock },
    alerte: { classe: 'text-red-700 font-medium', Icone: AlertTriangle },
  };
  const { classe, Icone } = TONS[verdict.ton];
  const muet = ancienneteTexte(consigne.consigne_muet_depuis_s);

  return (
    <div
      className={`flex items-start gap-1.5 ${classe}`}
      title={verdict.detail}
      data-testid="battery-hold-verdict"
    >
      <Icone className="w-3 h-3 mt-0.5 flex-shrink-0" />
      <span>
        {verdict.libelle}
        {muet && <span className="font-normal"> · sans nouvelles depuis {muet}</span>}
      </span>
    </div>
  );
}
